import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import type { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'

import { MarketDate } from '@/features/bidding/utils/date/marketDate'

export type BidDatePickerProps = Readonly<{
  value: MarketDate | null
  onChange: (next: MarketDate | null) => void
}>

/**
 * Non-RHF MUI DatePicker wrapper for the bid detail toolbar. The selected
 * date is URL-synced by the parent (see `useBidDetailQueryParams`), not by
 * this component.
 *
 * Models after `PastBidDateSelectorToolbar` (already in the bidding feature)
 * — uses `AdapterLuxon` for i18n + `MarketDate` at the value boundary.
 */
export default function BidDatePicker({ value, onChange }: BidDatePickerProps) {
  const { t, i18n } = useTranslation()

  const handleChange = (date: DateTime | null) => {
    onChange(date ? new MarketDate(date) : null)
  }

  return (
    <LocalizationProvider adapterLocale={i18n.language} dateAdapter={AdapterLuxon}>
      <DatePicker
        enableAccessibleFieldDOMStructure={false}
        format="DD"
        label={t('bidding.results.bid_date')}
        slotProps={{ textField: { size: 'small', sx: { minWidth: 180 } } }}
        value={value?.getStartOfDay() ?? null}
        onChange={handleChange}
      />
    </LocalizationProvider>
  )
}
